import React, { KeyboardEvent, ChangeEvent, useCallback, useMemo, useRef, useState, useEffect } from 'react';

import {
	makeTime,
	useClassNames,
} from '@common';

import { filter } from 'lodash';

import {
	SearchIcon,
	RemoveIcon,
	AdvancedSearchIcon,
} from '@icons';

import './Search.scss';
import './MeetingSearch.scss';
import { IconButton } from '../IconButton';
import { Button, DayPickerInput, FormItem, ParticipantSelect, Space, StatusSelect, Textbox } from '@components';
import { apiInstance, MeetingListSearchQuery, MeetingListSearchType, MeetingStatuses, MeetingStatusInfo, ParticipantInfo } from '@api';
import { meetingStoreInitialState } from '@store';
import moment from 'moment';

interface MeetingSearchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
	leading?: JSX.Element,
	trailing?: JSX.Element,
	initialValue: MeetingListSearchQuery,
	onChangeSearch: (s: MeetingListSearchQuery) => void,
}

const handleStringTime = (value: string): Date => {
	const parts = value && value.split(':');
	if (!parts || parts.length != 2) {
		return;
	}

	const hours = parseInt(parts[0]) || 0;
	const minutes = parseInt(parts[1]) || 0;

	if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
		return;
	}

	const time = moment()
		.startOf('day')
		.add(hours, 'hours')
		.add(minutes, 'minutes')
		.toDate();

	return time;
};

function MeetingSearch(props: MeetingSearchProps): JSX.Element {
	const {
		className,
		initialValue,
		onChangeSearch,
		...otherProps
	} = props;

	const
		inputRef = useRef<HTMLInputElement>(null),
		[displayState, setDisplayState] = useState({'focused': false, 'advanced': false}),
		[searchQuery, setSearchQuery] = useState<MeetingListSearchQuery>(initialValue);

	const [preventBlur, setPreventBlur] = useState<boolean>(false);

	const 
		advancedViewRef = useRef(null),
		advancedViewButtonRef = useRef(null);

	const onFocus = useCallback((e?: React.FocusEvent<HTMLInputElement>): void => {
		setTimeout(() => setDisplayState({'focused': true, 'advanced': false}), 200);
		props.onFocus && props.onFocus(e);
	}, [props]);

	const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>): void => {
		if (preventBlur) {
			return;
		}

		setTimeout(() => setDisplayState({'focused': false, 'advanced': false}), 200);
		props.onBlur && props.onBlur(e);
	}, [props, preventBlur]);

	const onShowAdvanced = useCallback((): void => {
		setTimeout(() => {
			setDisplayState({'focused': true, 'advanced': true})
		});
	}, []);

	const [allStatuses, setAllStatuses] = useState<MeetingStatusInfo[]>([]);

	useEffect(() => {
		const fetchStatuses = async () => {
			let statuses = await apiInstance.getAllStatuses();
			statuses = filter(statuses, v => v.alias != MeetingStatuses.Upload);
			setAllStatuses(statuses);
		}

		fetchStatuses();
	}, []);


	const onAdvancedSearch = useCallback((): void => {
		const newQuery = new MeetingListSearchQuery({...searchQuery, ...{
			'quickSearch': '', 
			'searchType': MeetingListSearchType.Advanced,
			'startDateUtc_Start': searchQuery.startDateUtc_Start && moment(searchQuery.startDateUtc_Start)
				.startOf('day')
				.utc()
				.toDate(),

			'startTimeUtc_Start': searchQuery.startTimeUtc_Start && moment(searchQuery.startTimeUtc_Start)
				.utc()
				.toDate(),

			'startDateUtc_End': searchQuery.startDateUtc_End && moment(searchQuery.startDateUtc_End)
				.endOf('day')
				.utc()
				.toDate(),

			'startTimeUtc_End': searchQuery.startTimeUtc_End && moment(searchQuery.startTimeUtc_End)
				.utc()
				.toDate(),
		}});

		setSearchQuery(newQuery);
		onChangeSearch(newQuery);
		
		setTimeout(() => setDisplayState({'focused': false, 'advanced': false}));
	}, [searchQuery, setSearchQuery, onChangeSearch]);

	const onAdvancedSearchClear = useCallback((): void => {
		const newQuery = new MeetingListSearchQuery({...meetingStoreInitialState.query, ...{
			'quickSearch': '', 
			'searchType': MeetingListSearchType.Quick,
		}});

		setSearchQuery(newQuery);
		onChangeSearch(newQuery);
	
	}, [setSearchQuery, onChangeSearch]);
	

	const handleChangeSearchQuery = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
		const newQuery = new MeetingListSearchQuery(
			{...searchQuery, ...{'quickSearch': e.target.value}}
		);
		setSearchQuery(newQuery);
	}, [setSearchQuery, searchQuery]);

	const handleResetSearchQuery = useCallback((): void => {
		const newQuery = new MeetingListSearchQuery({
			...searchQuery, 
			...{ 'searchType': MeetingListSearchType.Quick,
					quickSearch: '' }
		});
		setSearchQuery(newQuery);
		onChangeSearch(newQuery);
	}, [setSearchQuery, onChangeSearch, searchQuery]);

	const onCloseSearch = useCallback((): void => {
		setTimeout(() => {
			setDisplayState({'focused': false, 'advanced': false})
		});
	}, [setDisplayState]);
	

	const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			const newQuery = new MeetingListSearchQuery({
				...searchQuery, 
				...{ 'searchType': MeetingListSearchType.Quick }
			});
			setSearchQuery(newQuery)
			onChangeSearch(newQuery);
		}
	}, [onChangeSearch, searchQuery]);

	const leadingView = useMemo(() => {
		const leading = props.leading || (
			<IconButton
				icon={<SearchIcon />}
				className={!displayState.focused && '-unfocused'}
				iconSize="20px"
				size="nogap"
			/>
		);

		return leading && (
			<div className="search__leading">
				{leading}
			</div>
		);
	}, [props.leading, displayState]);

	const adnvacedFiltersCount = useMemo(() => {
		if (searchQuery.searchType != MeetingListSearchType.Advanced) {
			return 0;
		}
		return (searchQuery.participants?.length ? 1 : 0) +
			(searchQuery.statuses?.length ? 1 : 0) +
			(searchQuery.subject ? 1 : 0) + 
			(searchQuery.taskUri ? 1 : 0) +
			(searchQuery.transcript ? 1 : 0) + 
			((searchQuery.startDateUtc_Start || searchQuery.startDateUtc_End) ? 1 : 0) +
			((searchQuery.startTimeUtc_Start || searchQuery.startTimeUtc_End) ? 1 : 0)
	}, [searchQuery]);

	const hasFilters = useMemo(() => {
		return (searchQuery.searchType == MeetingListSearchType.Quick && searchQuery.quickSearch) 
			|| adnvacedFiltersCount > 0;
	}, [searchQuery, adnvacedFiltersCount]);

	const trailingView = useMemo(() => {
		const
			trailing = props.trailing || (<>
				{hasFilters && <IconButton
					icon={<RemoveIcon />}
					className={!displayState.focused && '-unfocused'}
					iconSize="20px"
					size="nogap"
					onClick={handleResetSearchQuery}
				/>}
				<div className='search__advanced__config'>
					<IconButton
						ref={advancedViewButtonRef}
						className={!displayState.focused && '-unfocused'}
						icon={<AdvancedSearchIcon />}
						iconSize="24px"
						size="nogap"
						title='Расширенный поиск'
						onMouseDown={() => setPreventBlur(true)}
						onMouseUp={() => setPreventBlur(false)}
						onClick={onShowAdvanced}
					/>
					{!displayState.focused && adnvacedFiltersCount  > 0 && <div className='count'>{adnvacedFiltersCount}</div>}
				</div>
				</>
			),
			showTrailing = !!trailing;

		return showTrailing && (
			<div className="search__trailing">
				{trailing}
			</div>
		);
	}, [handleResetSearchQuery, onShowAdvanced, props.trailing, displayState, hasFilters, adnvacedFiltersCount]);

	const classes = useClassNames(
		`search ${displayState.focused ? '-focused' : ''}`,
		className,
	);

	const onChangeSubject = useCallback((value: string): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'subject': value }}));
	}, [setSearchQuery, searchQuery]);

	const onChangeTranscript = useCallback((value: string): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'transcript': value }}));
	}, [setSearchQuery, searchQuery]);

	const onChangeTaskUri = useCallback((value: string): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'taskUri': value }}));
	}, [setSearchQuery, searchQuery]);

	const onChangeParticipants = useCallback((values: ParticipantInfo[]): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'participants': values }}));
	}, [setSearchQuery, searchQuery]);

	const onChangeStartDateUtc_Start = useCallback((value: Date): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'startDateUtc_Start': value }}));
	}, [setSearchQuery, searchQuery]);

	const onChangeStartDateUtc_End = useCallback((value: Date): void => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{'startDateUtc_End': value }}));
	}, [setSearchQuery, searchQuery]);

	const startTime_Start = useMemo(() => {
		return (searchQuery.startTimeUtc_Start && makeTime(moment(searchQuery.startTimeUtc_Start))) || '';
	}, [searchQuery]);

	const startTime_End = useMemo(() => {
		return (searchQuery.startTimeUtc_End && makeTime(moment(searchQuery.startTimeUtc_End))) || '';
	}, [searchQuery]);

	const onChangeStartTimeUtc_Start = useCallback((value: string): void => {
		const time = handleStringTime(value);

		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{
			'startTimeUtc_Start': time,
		}}));
	}, [setSearchQuery, searchQuery]);

	const onChangeStartTimeUtc_End = useCallback((value: string): void => {
		const time = handleStringTime(value);

		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{
			'startTimeUtc_End': time,
		}}));
	}, [setSearchQuery, searchQuery]);

	const onChangeStatuses = useCallback((value: MeetingStatusInfo[]) => {
		setSearchQuery(new MeetingListSearchQuery({...searchQuery, ...{
			'statuses': value,
		}}));
	}, [setSearchQuery, searchQuery]);


	const advancedView = useMemo(() => {

		return displayState.advanced && <div className='search__advanced' ref={advancedViewRef}>
			<div className='search__advanced__table'>
				<div className='search__advanced__col'>
					<FormItem label="Название">
						<Textbox value={searchQuery.subject} onValueChange={onChangeSubject} />
					</FormItem>
					<FormItem label="Статус">
						<StatusSelect 
							values={searchQuery.statuses}
							suggestedValues={allStatuses} 
							onChange={onChangeStatuses}
						/>
					</FormItem>
				</div>
				<div className='search__advanced__col'>
					<FormItem label="Участники">
						<ParticipantSelect values={searchQuery.participants} suggestedValues={[]} onChange={onChangeParticipants} />
					</FormItem>
					<FormItem label="Текст стенограммы ">
						<Textbox value={searchQuery.transcript} onValueChange={onChangeTranscript} />
					</FormItem>
				</div>
				<div className='search__advanced__col'>
					<FormItem label="Задания на исполнение">
						<Textbox value={searchQuery.taskUri} onValueChange={onChangeTaskUri} />
					</FormItem>
					<Space>
						<FormItem label="Дата проведения">
							<Space>
								<DayPickerInput value={searchQuery.startDateUtc_Start} onDayChange={onChangeStartDateUtc_Start} />
								<div className="time-interval">—</div>
								<DayPickerInput value={searchQuery.startDateUtc_End} onDayChange={onChangeStartDateUtc_End} />
							</Space>
						</FormItem>
						<FormItem label="Время проведения">
							<Space>
								<Textbox type='time' className='timebox' value={startTime_Start} onValueChange={onChangeStartTimeUtc_Start} />
								<div className="time-interval">—</div>
								<Textbox type='time' className='timebox' value={startTime_End} onValueChange={onChangeStartTimeUtc_End} />
							</Space>
						</FormItem>
					</Space>
				</div>
			</div>
			<div className="search__advanced__buttons">
				<Space>
					<Button theme="primary" onClick={onAdvancedSearch}>Найти</Button>
					<Button theme="ghost" onClick={onAdvancedSearchClear}>Очистить поиск</Button>
				</Space>
			<div className='right'>
				<Button theme="ghost" onClick={onCloseSearch}>Закрыть поиск</Button>
			</div>
			</div>
		</div>
	}, [displayState, onAdvancedSearch, onAdvancedSearchClear, onChangeParticipants,
		onChangeStartDateUtc_Start, onChangeStartDateUtc_End, startTime_Start, startTime_End,
		onChangeStartTimeUtc_End, onChangeSubject, onChangeTranscript, onChangeStartTimeUtc_Start, 
		onChangeTaskUri, searchQuery, onCloseSearch, allStatuses, onChangeStatuses]);

	return (
		<div className={classes}>
			{leadingView}
			<input
				{...otherProps}
				className="search__input"
				onChange={handleChangeSearchQuery}
				onKeyDown={handleKeyDown}
				value={searchQuery.quickSearch}
				onFocus={onFocus}
				onBlur={onBlur}
				ref={inputRef}
				type="search"
			/>
			{trailingView}
			{advancedView}
		</div>
	);
}

const MemoisedSearch = React.memo(MeetingSearch);

export {MemoisedSearch as MeetingSearch};